import { clearHighlightLines, getDropData, formatPlace, addPlaceIfNotExists } from './utils';
import { SAVED_PLACES_COLUMN_ID } from './Constants';

let scrollContainerRef = null;
let scrollDirection = 0;
let scrollAnimationFrameId = null;
let previousZone = 'center';

export const setScrollContainerRef = (ref) => {
  scrollContainerRef = ref;
};

const addDragOverClass = (element) => {
  element.classList.add('drag-over');
};

const removeDragOverClass = (element) => {
  element.classList.remove('drag-over');
};

export const highlightColumn = (columnId, highlightedColumnId, setHighlightedColumnId, savedPlacesRef) => {
  if (highlightedColumnId !== columnId) {
    let column;
    let title;
    if (columnId === SAVED_PLACES_COLUMN_ID) {
      column = savedPlacesRef.current.querySelector('.saved-places-box');
      title = document.querySelector('.saved-places-title');
    } else {
      column = document.getElementById(columnId);
      title = document.querySelector(`#column-title-${columnId}`);
    }
    if (column) {
      addDragOverClass(column);
      if (title) addDragOverClass(title);
      setHighlightedColumnId(columnId);
    }
  }
};

const scrollSpeed = 5;
const scrollZoneRatio = 0.3;

const performScroll = () => {
  if (!scrollContainerRef || !scrollContainerRef.current || scrollDirection === 0) {
    scrollAnimationFrameId = null;
    return;
  }

  const container = scrollContainerRef.current;
  container.scrollLeft += scrollDirection * scrollSpeed;
  scrollAnimationFrameId = requestAnimationFrame(performScroll);
};

const startScrolling = (direction) => {
  if (scrollDirection === direction) return;
  scrollDirection = direction;
  if (scrollAnimationFrameId === null) {
    scrollAnimationFrameId = requestAnimationFrame(performScroll);
  }
};

const stopScrolling = () => {
  scrollDirection = 0;
  if (scrollAnimationFrameId) {
    cancelAnimationFrame(scrollAnimationFrameId);
    scrollAnimationFrameId = null;
  }
};

const determineZone = (xPosition, rect) => {
  if (xPosition === 0) return 'center';
  const scrollZoneWidth = rect.width * scrollZoneRatio;
  if (xPosition < rect.left + scrollZoneWidth) {
    return 'left';
  } else if (xPosition > rect.right - scrollZoneWidth) {
    return 'right';
  } else {
    return 'center';
  }
};

const checkHorizontalScroll = (xPosition) => {
  if (!scrollContainerRef || !scrollContainerRef.current) {
    stopScrolling();
    return;
  }

  const container = scrollContainerRef.current;
  const rect = container.getBoundingClientRect();
  const currentZone = determineZone(xPosition, rect);

  if (currentZone === 'left') {
    if (previousZone !== 'left') {
      startScrolling(-1);
    }
  } else if (currentZone === 'right') {
    if (previousZone !== 'right') {
      startScrolling(1);
    }
  } else {
    if (previousZone !== 'center') {
      stopScrolling();
    }
  }

  previousZone = currentZone;
};

const highlightWidget = (e, columnId) => {
  clearHighlightLines();
  if (columnId !== SAVED_PLACES_COLUMN_ID) {
    const targetElement = e.target.closest('[data-index]');
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const offset = e.clientY - rect.top;
      const height = rect.height;
      const newBorderTop = offset < height / 2 ? '2px solid #007BFF' : '';
      const newBorderBottom = offset >= height / 2 ? '2px solid #007BFF' : '';

      targetElement.style.borderTop = newBorderTop;
      targetElement.style.borderBottom = newBorderBottom;
    }
  }
};

export const handleDragOver = (e, columnId, highlightedColumnId, setHighlightedColumnId, savedPlacesRef, liveRegion, scrollRef) => {
  e.preventDefault();
  setScrollContainerRef(scrollRef);
  highlightColumn(columnId, highlightedColumnId, setHighlightedColumnId, savedPlacesRef);
  highlightWidget(e, columnId);
  checkHorizontalScroll(e.clientX);
};

export const handleDragLeave = (e, columnId, setHighlightedColumnId, liveRegion, savedPlacesRef) => {
  requestAnimationFrame(() => {
    let column;
    let title;
    if (columnId === SAVED_PLACES_COLUMN_ID) {
      column = savedPlacesRef.current.querySelector('.saved-places-box');
      title = document.querySelector('.saved-places-title');
    } else {
      column = document.getElementById(columnId);
      title = document.querySelector(`#column-title-${columnId}`);
    }
    const targetElement = e.relatedTarget;
    if (column && !column.contains(targetElement)) {
      removeDragOverClass(column);
      if (title) removeDragOverClass(title);
      setHighlightedColumnId(null);
    }
    if (targetElement) {
      targetElement.style.borderTop = '';
      targetElement.style.borderBottom = '';
    }
  });
  stopScrolling();
  previousZone = 'center';
};

export const handleDrop = (
  e,
  columnId,
  highlightedColumnId,
  setHighlightedColumnId,
  liveRegion,
  savedPlacesRef,
  setColumns,
  setColumnsToSave,
  savePlace,
  savedPlaces,
  selectedItinerary,
  handleRemoveWidget,
  handleAddWidget
) => {
  handleDragLeave(e, columnId, setHighlightedColumnId, liveRegion, savedPlacesRef);
  clearHighlightLines();

  document.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));

  const dropData = getDropData(e, liveRegion);
  if (!dropData) return;

  const place = formatPlace(dropData);
  const targetElement = e.target.closest('[data-index]');
  let destinationIndex = targetElement ? parseInt(targetElement.dataset.index, 10) : null;

  if (columnId !== SAVED_PLACES_COLUMN_ID && targetElement) {
    const rect = targetElement.getBoundingClientRect();
    const offset = e.clientY - rect.top;
    const height = rect.height;
    if (offset >= height / 2) {
      destinationIndex += 1;
    }
  }

  if (columnId === dropData.columnId) {
    handleRemoveWidget(dropData.columnId, dropData.placeData, true, true);
    handleAddWidget(columnId, place, destinationIndex, true);
    if (liveRegion) liveRegion.textContent = `Moved widget within ${columnId}`;
    previousZone = 'center';
    return;
  }

  handleRemoveWidget(dropData.columnId, dropData.placeData, false, true);
  handleAddWidget(columnId, place, destinationIndex, false);

  if (columnId === SAVED_PLACES_COLUMN_ID) {
    const placeExists = addPlaceIfNotExists(dropData.placeData, selectedItinerary, savedPlaces, savePlace);
    if (!placeExists && liveRegion) liveRegion.textContent = `Added ${dropData.placeData.name} to saved places.`;
  }
  stopScrolling();
  previousZone = 'center';
};

export const scrollHorizontallyOnTouchMove = (xPosition) => {
  checkHorizontalScroll(xPosition);
};
